import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 60 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, delay, ease: "easeOut" },
});

const values = [
  {
    title: "Vertrauen",
    desc: "Jede Session findet in einem sicheren, vertraulichen Rahmen statt. Was du teilst, bleibt bei mir.",
  },
  {
    title: "Klarheit",
    desc: "Ich arbeite klar, professionell und ohne esoterisches Beiwerk. Energie ist real — und wirksam.",
  },
  {
    title: "Transformation",
    desc: "Echte Veränderung braucht Tiefe. Ich begleite dich dahin, wo Heilung wirklich stattfinden kann.",
  },
];

export default function UeberAuralia() {
  return (
    <div className="bg-[#050510] text-[#F0EDE8] min-h-screen pt-32 pb-24">
      {/* ── INTRO ── */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div {...fadeUp()}>
            <p className="text-[0.65rem] uppercase tracking-[0.4em] text-[#C9A84C] mb-4">
              Über Auralia
            </p>
            <h1
              className="text-5xl md:text-6xl font-light text-[#F0EDE8] mb-8 leading-tight"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              Heilung, die wirklich tief geht.
            </h1>
            <div className="space-y-5 text-[#9A9A9A] leading-relaxed">
              <p>
                Auralia steht für eine ernsthafte, einfühlsame und hochprofessionelle
                Begleitung auf deinem Weg zu mehr innerer Stabilität, Klarheit und
                Wohlbefinden. Remote — von überall auf der Welt.
              </p>
              <p>
                Meine Arbeit verbindet langjährige Erfahrung in energetischen Methoden
                mit einem tiefen Verständnis für das menschliche Energiesystem. Dabei
                ist mir wichtig: keine Esoterik-Klischees, kein Kitsch — sondern klare,
                wirksame Arbeit auf feinstofflicher Ebene.
              </p>
              <p>
                Jede Session ist individuell auf dich abgestimmt. Ich höre zu, nehme
                wahr und arbeite mit dem, was wirklich gebraucht wird.
              </p>
            </div>
            <div className="mt-10">
              <Link
                to="/leistungen"
                className="btn-gold-shimmer border border-[#C9A84C] text-[#C9A84C] text-xs uppercase tracking-[0.2em] px-10 py-4 hover:bg-[#C9A84C] hover:text-[#050510] transition-all duration-300"
                data-testid="about-leistungen-btn"
              >
                Meine Leistungen
              </Link>
            </div>
          </motion.div>

          {/* Portrait placeholder */}
          <motion.div
            className="relative flex justify-center"
            {...fadeUp(0.2)}
          >
            <div className="relative w-80 h-96 rounded-2xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1498805983167-a523078d762c?w=640&auto=format&fit=crop"
                alt="Auralia – Energetische Heilung"
                className="w-full h-full object-cover"
                style={{ filter: "brightness(0.75) saturate(0.8)" }}
              />
              <div
                className="absolute inset-0 rounded-2xl"
                style={{
                  background:
                    "linear-gradient(to top, rgba(5,5,16,0.7) 0%, transparent 60%)",
                }}
              />
              <div
                className="absolute inset-0 rounded-2xl border border-[#C9A84C]/20"
                style={{ boxShadow: "0 0 40px rgba(155,114,207,0.15)" }}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── APPROACH ── */}
      <section className="border-y border-[#1A1A2E] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div className="text-center mb-16" {...fadeUp()}>
            <p className="text-[0.65rem] uppercase tracking-[0.4em] text-[#C9A84C] mb-4">
              Philosophie
            </p>
            <h2
              className="text-4xl md:text-5xl font-light text-[#F0EDE8]"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              Mein Ansatz
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              className="space-y-6 text-[#9A9A9A] text-lg leading-relaxed"
              {...fadeUp(0.1)}
            >
              <p>
                Energie ist überall. Sie verbindet uns — mit uns selbst, mit anderen,
                mit dem Leben. Wenn der Energiefluss gestört ist, zeigt sich das auf
                allen Ebenen: körperlich, emotional, mental.
              </p>
              <p>
                Meine Arbeit setzt genau dort an: bei den Ursachen, nicht nur den
                Symptomen. Durch gezielte energetische Arbeit können Blockaden gelöst,
                Muster erkannt und echte Transformation ermöglicht werden.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── VALUES ── */}
      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12">
        <motion.div className="text-center mb-16" {...fadeUp()}>
          <p className="text-[0.65rem] uppercase tracking-[0.4em] text-[#C9A84C] mb-4">
            Werte
          </p>
          <h2
            className="text-4xl md:text-5xl font-light text-[#F0EDE8]"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Was mir wichtig ist
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {values.map(({ title, desc }, i) => (
            <motion.div
              key={title}
              className="text-center p-10 bg-[#0D0D1A] border border-[#1A1A2E] rounded-2xl"
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: i * 0.15 }}
              data-testid={`value-${title.toLowerCase()}`}
            >
              <div
                className="text-4xl text-[#C9A84C]/30 mb-6 font-light"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
              >
                {String(i + 1).padStart(2, "0")}
              </div>
              <h3
                className="text-2xl font-light text-[#F0EDE8] mb-4"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
              >
                {title}
              </h3>
              <p className="text-[#9A9A9A] text-sm leading-relaxed">{desc}</p>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
